(() => {
  const SELECTOR = "[data-dpp-featured-builds]";
  const COMPACT_LAYOUT_QUERY = "(max-width: 899px)";

  class DppFeaturedBuildsController {
    constructor(element) {
      this.element = element;
      this.rows = [];
      this.lastWidth = 0;
      this.firstBackgroundVersion = 0;
      this.update = this.update.bind(this);
      this.refresh = this.refresh.bind(this);

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(element);
      } else {
        window.addEventListener("resize", this.update, { passive: true });
      }

      if ("MutationObserver" in window) {
        this.mutationObserver = new MutationObserver((mutations) => {
          if (mutations.some((mutation) => mutation.type === "childList")) {
            this.refresh();
          }
        });
        this.mutationObserver.observe(element, { childList: true, subtree: true });
      }

      this.refresh();
    }

    refresh() {
      this.rows = Array.from(this.element.querySelectorAll(".dpp-featured-builds__row"));
      this.lastWidth = 0;
      this.update(true);
      this.waitForFirstBackground();
    }

    waitForFirstBackground() {
      const version = ++this.firstBackgroundVersion;
      const firstBackground = this.rows[0]?.querySelector(".dpp-featured-builds__background");
      this.element.classList.remove("dpp-featured-builds--first-background-ready");

      const showFirstPanelAccent = () => {
        if (version !== this.firstBackgroundVersion) return;

        requestAnimationFrame(() => {
          if (version !== this.firstBackgroundVersion) return;
          this.update(true);
          this.element.classList.add("dpp-featured-builds--first-background-ready");
        });
      };

      if (!firstBackground || firstBackground.complete) {
        showFirstPanelAccent();
        return;
      }

      firstBackground.addEventListener("load", showFirstPanelAccent, { once: true });
      firstBackground.addEventListener("error", showFirstPanelAccent, { once: true });
    }

    update(force = false) {
      const viewportWidth = Math.round(this.element.getBoundingClientRect().width);
      if (!viewportWidth || (!force && viewportWidth === this.lastWidth)) return;

      this.lastWidth = viewportWidth;
      const isCompact = window.matchMedia(COMPACT_LAYOUT_QUERY).matches;
      const baseCanvasWidth = Number(this.element.dataset.canvasWidth) || 1450;
      const basePanelHeight = Number(this.element.dataset.panelHeight) || 370;
      const basePanelGap = Number(this.element.dataset.panelGap) || 0;
      const baseSlantDepth = Number(this.element.dataset.slantDepth) || 0;
      const effectiveSlantDepth = isCompact
        ? Math.min(baseSlantDepth, 18)
        : baseSlantDepth;
      let firstSceneScale = 1;

      this.rows.forEach((row, index) => {
        const sourceWidth = Number(row.dataset.sceneWidth) || 2500;
        const sourceHeight = Number(row.dataset.sceneHeight) || 450;
        const sceneScale = isCompact ? 1 : Math.max(1, viewportWidth / sourceWidth);
        const slantDirection = row.classList.contains(
          isCompact
            ? "dpp-featured-builds__row--mobile-down-left"
            : "dpp-featured-builds__row--down-left",
        )
          ? -1
          : row.classList.contains(
                isCompact
                  ? "dpp-featured-builds__row--mobile-down-right"
                  : "dpp-featured-builds__row--down-right",
              )
            ? 1
            : 0;
        const scaledSlantDepth = slantDirection === 0
          ? 0
          : effectiveSlantDepth * sceneScale;
        const slantAngle = slantDirection
          * (Math.atan2(scaledSlantDepth, viewportWidth) * 180 / Math.PI);

        if (index === 0) firstSceneScale = sceneScale;

        row.style.setProperty("--dpp-build-scene-scale", sceneScale.toFixed(5));
        row.style.setProperty("--dpp-build-scene-width", `${sourceWidth * sceneScale}px`);
        row.style.setProperty("--dpp-build-scene-height", `${sourceHeight * sceneScale}px`);
        row.style.setProperty("--dpp-build-panel-height-scaled", `${basePanelHeight * sceneScale}px`);
        row.style.setProperty("--dpp-build-panel-gap-scaled", `${basePanelGap * sceneScale}px`);
        row.style.setProperty("--dpp-build-stage-width", `${baseCanvasWidth * sceneScale}px`);
        row.style.setProperty("--dpp-build-slant-scaled", `${scaledSlantDepth}px`);
        row.style.setProperty("--dpp-build-slant-angle", `${slantAngle.toFixed(5)}deg`);
        row.style.setProperty(
          "--dpp-build-truck-x-scaled",
          `${(Number(row.dataset.truckX) || 0) * sceneScale}px`,
        );
        row.style.setProperty(
          "--dpp-build-truck-width-scaled",
          `${(Number(row.dataset.truckWidth) || 950) * sceneScale}px`,
        );
        row.style.setProperty(
          "--dpp-build-truck-bottom-scaled",
          `${(Number(row.dataset.truckBottom) || 0) * sceneScale}px`,
        );
        const configuredCopyX = (Number(row.dataset.contentX) || 0) * sceneScale;
        let resolvedCopyX = configuredCopyX;

        if (!isCompact) {
          const copy = row.querySelector(".dpp-featured-builds__copy");
          const copyWidth = copy?.getBoundingClientRect().width || 340;
          const edgeGutter = viewportWidth < 1100 ? 28 : 40;
          const maximumOffset = Math.max(
            0,
            viewportWidth / 2 - copyWidth / 2 - edgeGutter,
          );

          resolvedCopyX = Math.sign(configuredCopyX || 1)
            * Math.min(Math.abs(configuredCopyX), maximumOffset);
        }

        row.style.setProperty("--dpp-build-copy-x-scaled", `${resolvedCopyX}px`);
      });

      this.element.style.setProperty(
        "--dpp-build-slant",
        `${effectiveSlantDepth * firstSceneScale}px`,
      );
    }
  }

  const initialize = (scope = document) => {
    const elements = [];
    if (scope.matches && scope.matches(SELECTOR)) elements.push(scope);
    if (scope.querySelectorAll) elements.push(...scope.querySelectorAll(SELECTOR));

    elements.forEach((element) => {
      if (element.dppFeaturedBuildsController) {
        element.dppFeaturedBuildsController.refresh();
      } else {
        element.dataset.dppFeaturedBuildsReady = "true";
        element.dppFeaturedBuildsController = new DppFeaturedBuildsController(element);
      }
    });
  };

  initialize();
  const refreshEditorSection = (event) => {
    requestAnimationFrame(() => {
      const section = event.target.closest && event.target.closest(SELECTOR);
      initialize(section || event.target);
    });
  };

  document.addEventListener("shopify:section:load", refreshEditorSection);
  document.addEventListener("shopify:section:select", refreshEditorSection);
  document.addEventListener("shopify:block:select", refreshEditorSection);
})();
